import React, { useMemo, useEffect } from "react"
import { NavLink, useLocation } from "react-router-dom"
import {
  Briefcase,
  Users,
  CheckSquare,
  FileText,
  Calendar,
  ChatCircle,
  CaretDown,
  CaretLeft,
  CaretRight,
  Kanban,
  UserPlus,
  ClipboardText,
  ShieldCheck,
  Question,
  ListChecks,
  Voicemail,
  CurrencyDollar,
  Clock,
  PhoneCall,
  ChartBar,
  SquaresFour,
  PenNib,
  Scales,
  FilePlus,
  Gavel,
  XCircle,
  ArrowsClockwise,
} from "@phosphor-icons/react"
import { useUIStore } from "../../stores"
import { useSidebarCounts, useIsDesktop, useCallCenterSocket, useDivisions } from "../../hooks"
import { cn } from "../../lib/utils"
import { AgentCommandCenter, QueueModal, IncomingCallOverlay } from "../call-center"
import type { SidebarCounts } from "../../types"

// Logo from public folder
const logoImage = process.env.PUBLIC_URL + "/logo.png"

interface NavItem {
  label: string
  href: string
  icon: React.ElementType
  badge?: number
  badgeColor?: "danger" | "warning" | "primary"
  children?: NavItem[]
}

interface NavSection {
  label: string
  items: NavItem[]
}

// Build navigation sections with dynamic badge counts
function buildNavSections(counts: SidebarCounts | null, divisionModules: string[]): NavSection[] {
  const sections: NavSection[] = [
    {
      label: "Workspace",
      items: [
        { label: "Pipeline", href: "/pipeline", icon: Kanban },
        { label: "Claimables", href: "/claimables", icon: ClipboardText },
        { label: "Matters", href: "/matters", icon: Briefcase },
        { label: "Contacts", href: "/contacts", icon: Users },
      ],
    },
    {
      label: "Tasks & Items",
      items: [
        {
          label: "Leads",
          href: "/leads",
          icon: UserPlus,
          badge: counts?.leads,
          badgeColor: "danger",
          children: [
            { label: "Unclaimed", href: "/leads/live", icon: UserPlus },
            { label: "Mine", href: "/leads/mine", icon: UserPlus },
            { label: "All", href: "/leads", icon: UserPlus },
          ],
        },
        {
          label: "Tasks",
          href: "/tasks",
          icon: CheckSquare,
          badge: counts?.tasks_overdue,
          badgeColor: "danger",
          children: [
            { label: "All Tasks", href: "/tasks", icon: CheckSquare },
            { label: "Today", href: "/tasks/today", icon: CheckSquare },
            { label: "Overdue", href: "/tasks/overdue", icon: CheckSquare },
          ],
        },
        {
          label: "Items",
          href: "/items",
          icon: ListChecks,
          badge: counts?.outstanding_items_overdue,
          badgeColor: "danger",
          children: [
            { label: "All Items", href: "/items", icon: ListChecks },
            { label: "Today", href: "/items/today", icon: ListChecks },
            { label: "Overdue", href: "/items/overdue", icon: ListChecks },
          ],
        },
        {
          label: "Approvals",
          href: "/approvals",
          icon: ShieldCheck,
          badge: counts?.approvals_pending,
          badgeColor: "warning",
          children: [
            { label: "Pending For Me", href: "/approvals/pending", icon: ShieldCheck },
            { label: "Returned To Me", href: "/approvals/returned", icon: ShieldCheck },
            { label: "Denied", href: "/approvals/denied", icon: ShieldCheck },
            { label: "My Requests", href: "/approvals/mine", icon: ShieldCheck },
          ],
        },
        {
          label: "Requests",
          href: "/requests",
          icon: Question,
          badge: counts?.client_requests_pending,
          badgeColor: "warning",
        },
        {
          label: "General Items",
          href: "/general-items",
          icon: ListChecks,
          badge: counts?.general_items_pending,
          badgeColor: "warning",
        },
        { label: "Time Entries", href: "/time-entries", icon: Clock },
        { label: "Collections", href: "/collections", icon: CurrencyDollar },
      ],
    },
    {
      label: "Communications",
      items: [
        { label: "Texts", href: "/texts", icon: ChatCircle, badge: counts?.texts, badgeColor: "danger" },
        {
          label: "Voicemail",
          href: "/voicemail",
          icon: Voicemail,
          badge: counts?.voicemails,
          badgeColor: "danger",
        },
        {
          label: "Documents",
          href: "/documents",
          icon: FileText,
          badge: counts?.documents_pending,
          badgeColor: "warning",
          children: [
            { label: "All", href: "/documents", icon: FileText },
            { label: "Pending", href: "/documents?type=pending", icon: FileText, badge: counts?.documents_pending, badgeColor: "warning" },
            { label: "Expired", href: "/documents?type=expired", icon: FileText, badge: counts?.documents_expired, badgeColor: "danger" },
          ],
        },
        { label: "Events", href: "/events", icon: Calendar, badge: counts?.events_today, badgeColor: "primary" },
       
        // {
        //   label: "Signing",
        //   href: "/signing/documents",
        //   icon: PenNib,
        //   children: [
        //     { label: "Documents", href: "/signing/documents", icon: PenNib },
        //     { label: "Templates", href: "/signing/templates", icon: PenNib },
        //     { label: "About", href: "/signing/access", icon: PenNib },
        //   ],
        // },
       
      ],
    },
    {
      label: "System",
      items: [
        { label: "Phone Calls", href: "/calls", icon: PhoneCall },
        { label: "Queue Metrics", href: "/queue-metrics", icon: ChartBar },
        { label: "Custom Views", href: "/custom-views", icon: SquaresFour },
      ],
    },
  ]

  // Conditionally add Modules section based on division settings
  const moduleItems: NavItem[] = []
  if (divisionModules.includes("bankruptcy")) {
    moduleItems.push({
      label: "Bankruptcy",
      href: "/modules/bk/new-case-entries",
      icon: Scales,
      children: [
        { label: "New Cases", href: "/modules/bk/new-case-entries", icon: FilePlus },
        { label: "Dismissed", href: "/modules/bk/dismissed-cases", icon: XCircle },
        { label: "Converted", href: "/modules/bk/converted-cases", icon: ArrowsClockwise },
        { label: "BK Items", href: "/modules/bk/items", icon: ListChecks },
        { label: "Trustee Docs", href: "/modules/bk/trustee-documents", icon: FileText },
        { label: "Hearings", href: "/modules/bk/hearings", icon: Gavel },
      ],
    })
  }
  if (moduleItems.length > 0) {
    sections.push({ label: "Modules", items: moduleItems })
  }

  return sections
}

// Badge component with refined styling
function Badge({ count, color }: { count: number; color: "danger" | "warning" | "primary" }) {
  const colorClasses = {
    danger: "bg-danger text-white",
    warning: "bg-warning text-white",
    primary: "bg-primary/80 text-white",
  }

  return (
    <span
      className={cn(
        "flex h-[16px] min-w-[16px] items-center justify-center rounded px-1 text-[9px] font-bold leading-none",
        colorClasses[color]
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  )
}

// Tooltip component for collapsed mode
function Tooltip({ children, label, badge, collapsed }: {
  children: React.ReactNode
  label: string
  badge?: number
  collapsed: boolean
}) {
  if (!collapsed) return <>{children}</>

  return (
    <div className="group relative">
      {children}
      <div className="sidebar-tooltip">
        {label}
        {badge !== undefined && badge > 0 && (
          <span className="ml-1.5 text-primary">({badge})</span>
        )}
      </div>
    </div>
  )
}

function NavItemComponent({
  item,
  collapsed,
  depth = 0,
}: {
  item: NavItem
  collapsed: boolean
  depth?: number
}) {
  const location = useLocation()
  const expandedNavItems = useUIStore((s) => s.expandedNavItems)
  const toggleNavItem = useUIStore((s) => s.toggleNavItem)
  const hasChildren = item.children && item.children.length > 0
  const isChildActive = hasChildren && item.children!.some(
    child => location.pathname.startsWith(child.href)
  )
  const isOpen = isChildActive || expandedNavItems.includes(item.label)

  // Expandable nav group (not collapsed)
  if (hasChildren && !collapsed) {
    return (
      <div>
        <button
          onClick={() => toggleNavItem(item.label)}
          className={cn(
            "group flex w-full items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[13px] font-medium transition-all duration-150",
            "text-sidebar-text hover:text-sidebar-text-active hover:bg-sidebar-hover",
            isOpen && "bg-sidebar-hover text-sidebar-text-active"
          )}
        >
          <item.icon className="h-[18px] w-[18px] shrink-0 opacity-75 group-hover:opacity-100 transition-opacity" />
          <span className="flex-1 text-left">{item.label}</span>
          <CaretDown
            className={cn(
              "h-3.5 w-3.5 opacity-50 transition-transform duration-200",
              isOpen && "rotate-180"
            )}
          />
          {item.badge !== undefined && item.badge > 0 && (
            <Badge count={item.badge} color={item.badgeColor || "danger"} />
          )}
        </button>
        <div
          className={cn(
            "overflow-hidden transition-all duration-200",
            isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          )}
        >
          <div className="ml-[15px] mt-1 space-y-0.5 border-l border-sidebar-border pl-2.5">
            {item.children?.map((child) => (
              <NavLink
                key={child.href}
                to={child.href}
                end
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 rounded-md px-2 py-[5px] text-[12px] font-medium transition-all duration-150",
                    isActive
                      ? "text-sidebar-text-active bg-sidebar-active"
                      : "text-sidebar-text-muted hover:text-sidebar-text-active hover:bg-sidebar-hover"
                  )
                }
              >
                <span>{child.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Regular nav item
  return (
    <Tooltip label={item.label} badge={item.badge} collapsed={collapsed}>
      <NavLink
        to={item.href}
        className={({ isActive }) =>
          cn(
            "group flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[13px] font-medium transition-all duration-150",
            collapsed ? "justify-center px-2" : "",
            isActive
              ? "nav-item-active text-sidebar-text-active bg-sidebar-active"
              : "text-sidebar-text hover:text-sidebar-text-active hover:bg-sidebar-hover"
          )
        }
      >
        <item.icon
          className={cn(
            "h-[18px] w-[18px] shrink-0 transition-opacity",
            "opacity-75 group-hover:opacity-100"
          )}
        />
        {!collapsed && (
          <>
            <span className="flex-1">{item.label}</span>
            {hasChildren && <CaretDown className="h-3.5 w-3.5 opacity-50" />}
            {item.badge !== undefined && item.badge > 0 && (
              <Badge count={item.badge} color={item.badgeColor || "danger"} />
            )}
          </>
        )}
      </NavLink>
    </Tooltip>
  )
}

// Section header component
function SectionHeader({ label, collapsed }: { label: string; collapsed: boolean }) {
  if (collapsed) return null

  return (
    <div className="px-3 pt-3 pb-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-sidebar-section-label">
        {label}
      </span>
    </div>
  )
}

export function Sidebar() {
  const { sidebarCollapsed, setSidebarCollapsed, mobileMenuOpen, setMobileMenuOpen, selectedDivision } = useUIStore()
  const { data: counts } = useSidebarCounts()
  const { data: divisions } = useDivisions()
  const location = useLocation()
  const isDesktop = useIsDesktop()

  // Initialize call center socket listeners for queue status updates
  useCallCenterSocket()

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname, setMobileMenuOpen])

  // Compute effective collapsed state based on screen size
  // Mobile/Tablet: always expanded (drawer is 280px)
  // Desktop: respect user preference
  const isCollapsed = isDesktop ? sidebarCollapsed : false

  // Resolve selected division's modules
  const divisionModules = useMemo(() => {
    if (!selectedDivision || !divisions) return []
    const div = divisions.find((d: any) => d._id === selectedDivision)
    return div?.modules ?? []
  }, [selectedDivision, divisions])

  // Build nav sections with dynamic counts
  const navSections = useMemo(() => buildNavSections(counts ?? null, divisionModules), [counts, divisionModules])

  return (
    <aside
      className={cn(
        "fixed inset-y-0 left-0 z-[200] flex flex-col transition-all duration-300 ease-out",
        "bg-gradient-to-b from-sidebar-gradient-from to-sidebar-gradient-to",
        "shadow-[4px_0_24px_rgba(0,0,0,0.15)] dark:shadow-[2px_0_8px_rgba(0,0,0,0.6)]",
        // Mobile/Tablet: drawer that slides in from left
        "w-[280px] -translate-x-full",
        mobileMenuOpen && "translate-x-0",
        // Desktop: always visible, respect collapse state
        "lg:translate-x-0",
        sidebarCollapsed ? "lg:w-16" : "lg:w-[200px]"
      )}
    >
      {/* Logo */}
      <div className="flex h-[56px] items-center justify-between border-b border-sidebar-border px-4">
        {!isCollapsed ? (
          <div className="flex items-center gap-3">
            <img src={logoImage} alt="IntuiDesk" className="h-8 w-8" />
            <span className="text-[16px] font-bold tracking-wide text-sidebar-text-active">
              INTUIDESK
            </span>
          </div>
        ) : (
          <div className="mx-auto">
            <img src={logoImage} alt="IntuiDesk" className="h-8 w-8" />
          </div>
        )}
      </div>

      {/* Agent Command Center - Call Center UI */}
      <AgentCommandCenter collapsed={isCollapsed} />

      {/* Incoming call overlay — always mounted so the portal is never
          destroyed by AgentCommandCenter's conditional rendering */}
      <IncomingCallOverlay />

      {/* Queue Modal - Rendered as portal */}
      <QueueModal />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 pb-3 scrollbar-dark">
        {navSections.map((section, index) => (
          <div key={section.label}>
            <SectionHeader label={section.label} collapsed={isCollapsed} />
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavItemComponent
                  key={item.href}
                  item={item}
                  collapsed={isCollapsed}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Collapse toggle - hidden on mobile/tablet, only shown on desktop */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className={cn(
          "hidden lg:flex h-[57px] items-center justify-center border-t border-sidebar-border",
          "text-sidebar-text-muted hover:text-sidebar-text-active hover:bg-sidebar-hover",
          "transition-colors duration-150"
        )}
      >
        {sidebarCollapsed ? (
          <CaretRight className="h-4 w-4" />
        ) : (
          <CaretLeft className="h-4 w-4" />
        )}
      </button>
    </aside>
  )
}
